import {
  CheckCircleIcon,
  CheckIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline'
import { FolderOpen, Save, Settings, Upload } from 'lucide-react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import { Button } from 'pcl-ui'
import React, { useEffect, useRef, useState } from 'react'
import { track } from 'track'
import { LoadDrawer } from '@/components/LoadDrawer'
import { SettingsDrawer } from '@/components/SettingsDrawer'
import { ShareDrawer } from '@/components/ShareDrawer'
import i18nextConfig from '@/next-i18next.config'
import { useAppDispatch, useAppSelector } from '@/state/hooks'
import { localSavedStatesStore } from '@/state/localSavedStatesStore'
import {
  clearOptions,
  ListType,
  OptionType,
  replaceMetaState,
} from '@/state/reducers'
import { getListStats } from '@/state/stateStats'
import { AppDispatch, persistor, store } from '@/state/store'
import {
  areThereUnsavedChanges,
  confirmUnsavedChanges,
} from '@/utils/compareState'
import { generateUUID } from '@/utils/uuid'

// Interval between autosaves for existing (already-saved) lists
const AUTOSAVE_INTERVAL_MS = 5 * 1000
// Delay before the first autosave for a brand-new unsaved list
const AUTOSAVE_INITIAL_DELAY_MS = 1 * 1000

type SaveStatus = 'saving' | 'unsaved' | 'saved'

async function generateDefaultFileName(
  options: OptionType[],
  fallback: string,
): Promise<string> {
  const existingNames = new Set<string>()
  await localSavedStatesStore.iterate<{ meta: { fileName?: string } }, void>(
    (item) => {
      if (item?.meta?.fileName) existingNames.add(item.meta.fileName)
    },
  )

  const base =
    options.length > 0
      ? (() => {
          const label = options[0].label
          return label.length > 20 ? `${label.slice(0, 20)}...` : label
        })()
      : fallback

  if (!existingNames.has(base)) return base

  let n = 2
  while (existingNames.has(`${base} ${n}`)) n++
  return `${base} ${n}`
}

const bgColorClasses = [
  'bg-cyan-950',
  'bg-yellow-700',
  'bg-red-900',
  'bg-green-900',
  'bg-indigo-900',
  'bg-purple-950',
  'bg-pink-800',
]

export const AppControls = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useRouter()
  const fileName = useAppSelector((state) => state.meta.fileName)
  const fileNameInputRef = React.useRef<HTMLInputElement>(null)
  const [editFileNameValue, setEditFileNameValue] = React.useState(fileName)
  useEffect(() => {
    setEditFileNameValue(fileName)
  }, [fileName])
  const [editingFileName, setEditingFileName] = React.useState(false)

  // Save status indicator
  const lastSavedSnapshotRef = useRef<string | null>(null)
  const lastAutoSaveAtRef = useRef<number>(0)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved')
  const options = useAppSelector((state) => state.options)
  const reasons = useAppSelector((state) => state.reasons)
  const metaKey = useAppSelector((state) => state.meta.key)
  const isSharedList = useAppSelector((state) => state.meta.isSharedList)
  const autoSave = useAppSelector((state) => state.settings.autoSave)

  // When a list is loaded or cleared (key changes), reset the baseline snapshot
  useEffect(() => {
    const state = store.getState()
    if (metaKey) {
      lastSavedSnapshotRef.current = JSON.stringify({
        options: state.options,
        reasons: state.reasons,
      })
    } else {
      lastSavedSnapshotRef.current = null
    }
    setSaveStatus('saved')
  }, [metaKey])

  // When options or reasons change, compare against the saved snapshot
  useEffect(() => {
    setSaveStatus((prev) => {
      if (prev === 'saving') return prev
      if (isSharedList) return 'saved'
      const isEmpty = !metaKey && options.length === 0
      if (isEmpty) return 'saved'
      if (lastSavedSnapshotRef.current === null) return 'unsaved'
      const current = JSON.stringify({ options, reasons })
      return current === lastSavedSnapshotRef.current ? 'saved' : 'unsaved'
    })
  }, [options, reasons, metaKey, isSharedList])
  // Track whether we've ever had a saved key this session, so we only clear the
  // hash when the user explicitly starts a new list — not on initial page load
  // (where the hash may be present for the home page to read and hydrate from).
  const hadKeyRef = useRef(false)

  // Sync meta.key to URL hash; redirect off slug pages when a shared list is saved
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (metaKey) {
      hadKeyRef.current = true
      const isOnSlugPage = /\/list\/[^/]+/.test(window.location.pathname)
      if (isOnSlugPage) {
        const locale = router.query.locale as string | undefined
        const homePath =
          locale && locale !== i18nextConfig.i18n.defaultLocale
            ? `/${locale}`
            : ''
        void router.replace(`${homePath}/#list=${metaKey}`)
      } else {
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}${window.location.search}#list=${metaKey}`,
        )
      }
    } else if (hadKeyRef.current && window.location.hash.startsWith('#list=')) {
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}`,
      )
    }
  }, [metaKey, router.query.locale])

  const handleDoubleClick = () => {
    if (!fileName) {
      track('event', 'double-click-empty-filename')
    } else {
      track('event', 'double-click-filename')
    }
    setEditingFileName(true)
  }

  // confirm unsaved changes on page unload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const currentState = store.getState()
      confirmUnsavedChanges(
        currentState,
        t(
          'AppControls.YouHaveUnsavedChangesDoYouWantToContinueAndDiscardChanges',
        ),
        'unload-page',
        currentState.settings?.askBeforeDiscardingUnsavedChanges ?? true,
      ).then((shouldDiscard) => {
        if (shouldDiscard) return
        else e.preventDefault()
      })
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [t])

  async function saveNewState(fileName: string, key: string, state: ListType) {
    if (fileName.length > 80) {
      setSaveStatus('unsaved')
      alert(t('AppControls.ErrorFilenameCannotBeLongerThan80Characters'))
      return
    }
    const timestamp = Date.now()
    const color =
      bgColorClasses[Math.floor(Math.random() * bgColorClasses.length)]
    const newState = {
      ...state,
      meta: { fileName, savedAt: timestamp, updatedAt: timestamp, key, color },
    }
    await localSavedStatesStore.setItem(key, newState)
    dispatch(replaceMetaState(newState.meta))
    lastSavedSnapshotRef.current = JSON.stringify({
      options: newState.options,
      reasons: newState.reasons,
    })
    setSaveStatus('saved')
  }

  async function saveState(state: ListType, key: string) {
    const newState = {
      ...state,
      meta: { ...state.meta, updatedAt: Date.now() },
    }
    await localSavedStatesStore.setItem(key, newState)
    dispatch(replaceMetaState(newState.meta))
    lastSavedSnapshotRef.current = JSON.stringify({
      options: newState.options,
      reasons: newState.reasons,
    })
    setSaveStatus('saved')
  }

  // Autosave on interval when enabled
  useEffect(() => {
    if (!autoSave) return
    const intervalId = setInterval(async () => {
      const state = store.getState()
      if (!state.meta.key && state.options.length === 0) return
      if (state.meta.isSharedList && !state.meta.key) return
      // For existing lists, throttle to AUTOSAVE_INTERVAL_MS
      if (
        state.meta.key &&
        Date.now() - lastAutoSaveAtRef.current < AUTOSAVE_INTERVAL_MS
      )
        return
      const hasUnsaved = await areThereUnsavedChanges(state)
      if (!hasUnsaved) return
      setSaveStatus('saving')
      await persistor.flush()
      try {
        if (state.meta.key) {
          await saveState(state, state.meta.key)
        } else {
          const name = await generateDefaultFileName(
            state.options,
            t('AppControls.defaultFileName'),
          )
          await saveNewState(name, generateUUID(), state)
        }
        lastAutoSaveAtRef.current = Date.now()
      } catch (err) {
        console.error('Autosave failed:', err)
        setSaveStatus('unsaved')
      }
    }, AUTOSAVE_INITIAL_DELAY_MS)
    return () => clearInterval(intervalId)
  }, [autoSave])

  async function promptAndSaveNewState(key: string) {
    const state = store.getState()
    const defaultName = await generateDefaultFileName(
      state.options,
      t('AppControls.defaultFileName'),
    )
    const promptDefault = state.meta.fileName || defaultName
    const fileName = window.prompt(
      t('AppControls.PleaseEnterAFileName'),
      promptDefault,
    )
    if (fileName && fileName.trim()) {
      await saveNewState(fileName, key, state)
    } else {
      setSaveStatus('unsaved')
    }
  }

  async function handleSubmitEditFileName(e: React.SyntheticEvent) {
    e.preventDefault()
    const state = store.getState()
    if (
      state.meta.fileName &&
      (!editFileNameValue || !editFileNameValue.trim())
    ) {
      alert(t('AppControls.ErrorFilenameCannotBeEmpty'))
      return
    }
    dispatch(replaceMetaState({ fileName: editFileNameValue }))
    track('event', 'edit-filename')
    const newState = {
      ...state,
      meta: { ...state.meta, fileName: editFileNameValue },
    }
    if (newState.meta.key) {
      await localSavedStatesStore.setItem(newState.meta.key, newState)
    }
    setEditingFileName(false)
  }

  function handleChangeEditFileName(e: React.FormEvent<HTMLInputElement>) {
    setEditFileNameValue(e.currentTarget.value)
  }

  async function handleClickSave() {
    setSaveStatus('saving')
    try {
      await persistor.flush()
      const state = store.getState()
      if (state.meta.key) {
        track('event', 'save-list', getListStats(state))
        await saveState(state, state.meta.key)
      } else {
        track('event', 'save-new-list', getListStats(state))
        await promptAndSaveNewState(generateUUID())
      }
    } catch (err) {
      console.error('Save failed:', err)
      setSaveStatus('unsaved')
    }
  }

  function closeEditFileName(name = '') {
    setEditingFileName(false)
    setEditFileNameValue(name)
  }

  const [isLoadDrawerOpen, setIsLoadDrawerOpen] = useState(false)
  const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState(false)
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false)
  const askBeforeDiscarding = useAppSelector(
    (state) => state.settings.askBeforeDiscardingUnsavedChanges,
  )

  function clearAll() {
    return (dispatch: AppDispatch) => {
      dispatch(clearOptions())
      dispatch(
        replaceMetaState({
          fileName: '',
          savedAt: undefined,
          updatedAt: undefined,
          key: undefined,
          color: '',
        }),
      )
    }
  }

  return (
    <div className="bg-surface-muted dark:bg-dark-bg2 transition-colors border-b-2 border-ink/20 dark:border-dark-border/20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <nav
          className="flex items-center justify-end py-3 gap-2"
          aria-label={t('AppControls.AppControls')}
        >
          {/* Filename left side */}
          <div className="flex-1 min-w-0">
            {editingFileName ? (
              <form className="flex gap-1" onSubmit={handleSubmitEditFileName}>
                <input
                  ref={fileNameInputRef}
                  type="text"
                  name="edit-filename"
                  data-test="edit-option-input"
                  className="flex-1 min-w-0 text-sm px-2 py-1 text-ink dark:text-dark-ink bg-transparent outline-none border-b-2 border-dashed border-ink dark:border-dark-border"
                  onChange={handleChangeEditFileName}
                  onKeyUp={(e) => {
                    if (e.key === 'Escape') closeEditFileName(fileName || '')
                  }}
                  value={editFileNameValue || ''}
                />
                <button
                  aria-label={t('AppControls.SaveUpdatedFileName')}
                  type="submit"
                  className="p-1 text-ink-secondary dark:text-dark-ink2 hover:text-pro dark:hover:text-pro-dark transition-colors"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                </button>
              </form>
            ) : fileName ? (
              <span
                data-test="filename-heading"
                onClick={handleDoubleClick}
                className="text-sm text-ink-secondary dark:text-dark-ink2 cursor-text truncate block"
              >
                {fileName}
              </span>
            ) : (
              <span
                onDoubleClick={handleDoubleClick}
                className="text-sm text-ink-secondary dark:text-dark-ink2 opacity-40 cursor-text"
              ></span>
            )}
          </div>

          {/* Buttons right side */}
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              data-test="new-button"
              aria-label={t('AppControls.New')}
              onClick={() => {
                confirmUnsavedChanges(
                  store.getState(),
                  t(
                    'AppControls.YouHaveUnsavedChangesDoYouWantToContinueAndDiscardChanges',
                  ),
                  'click-new',
                  askBeforeDiscarding,
                ).then((shouldDiscard) => {
                  if (shouldDiscard) {
                    dispatch(clearAll())
                    closeEditFileName()
                    window.dispatchEvent(new CustomEvent('pcl:new-list'))
                    if (/\/list\/[^/]+/.test(window.location.pathname)) {
                      const locale = router.query.locale as string | undefined
                      const home =
                        locale && locale !== i18nextConfig.i18n.defaultLocale
                          ? `/${locale}`
                          : '/'
                      router.push(home)
                    }
                  }
                })
              }}
            >
              <DocumentIcon
                className="h-3 w-3"
                aria-hidden="true"
                strokeWidth={1.8}
              />
              <span className="hidden sm:inline">{t('AppControls.New')}</span>
            </Button>

            <Button
              data-test="load-button"
              aria-label={t('AppControls.Load')}
              onClick={() => {
                track('event', 'open-load-drawer')
                setIsLoadDrawerOpen(true)
                closeEditFileName()
              }}
            >
              <FolderOpen
                className="h-3 w-3"
                aria-hidden="true"
                strokeWidth={1.8}
              />
              <span className="hidden sm:inline">{t('AppControls.Load')}</span>
            </Button>

            <Button
              data-test="save-button"
              aria-label={t('AppControls.Save')}
              disabled={saveStatus === 'saved' && !!metaKey}
              onClick={() => {
                handleClickSave()
                closeEditFileName()
              }}
            >
              <div className="relative shrink-0">
                <Save
                  className="h-3 w-3"
                  aria-hidden="true"
                  strokeWidth={1.8}
                />
                {saveStatus === 'saving' && (
                  <span
                    data-test="save-status-saving"
                    className="absolute -top-1 -right-1 block h-2 w-2 rounded-full bg-amber-400 animate-pulse ring-1 ring-surface dark:ring-dark-bg"
                  />
                )}
                {saveStatus === 'unsaved' && (
                  <span
                    data-test="save-status-unsaved"
                    className="absolute -top-1 -right-1 block h-2 w-2 rounded-full bg-con dark:bg-con-dark ring-1 ring-surface dark:ring-dark-bg"
                  />
                )}
                {saveStatus === 'saved' && metaKey && (
                  <CheckIcon
                    data-test="save-status-saved"
                    className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 text-pro dark:text-pro-dark"
                    strokeWidth={3}
                  />
                )}
              </div>
              <span className="hidden sm:inline">{t('AppControls.Save')}</span>
            </Button>

            {process.env.FEATURES_SHOW_SETTINGS_MENU === 'true' && (
              <Button
                data-test="settings-button"
                aria-label={t('Settings.title')}
                onClick={() => {
                  track('event', 'open-settings-drawer')
                  setIsSettingsDrawerOpen(true)
                }}
              >
                <Settings
                  className="h-3 w-3"
                  aria-hidden="true"
                  strokeWidth={1.8}
                />
                <span className="hidden sm:inline">{t('Settings.title')}</span>
              </Button>
            )}

            {process.env.FEATURES_SHOW_SHARE_BUTTON === 'true' && (
              <Button
                variant="cyan"
                data-test="share-button"
                aria-label={t('Share.title', { context: 'action' })}
                disabled={options.length === 0}
                onClick={() => {
                  track('event', 'open-share-drawer')
                  setIsShareDrawerOpen(true)
                }}
              >
                <Upload
                  className="h-3 w-3"
                  aria-hidden="true"
                  strokeWidth={1.8}
                />
                <span className="hidden sm:inline" aria-hidden="true">
                  {t('Share.title', { context: 'action' })}
                </span>
              </Button>
            )}

            <LoadDrawer
              isOpen={isLoadDrawerOpen}
              onClose={() => setIsLoadDrawerOpen(false)}
            />
            {process.env.FEATURES_SHOW_SHARE_BUTTON === 'true' && (
              <ShareDrawer
                isOpen={isShareDrawerOpen}
                onClose={() => setIsShareDrawerOpen(false)}
              />
            )}
            {process.env.FEATURES_SHOW_SETTINGS_MENU === 'true' && (
              <SettingsDrawer
                isOpen={isSettingsDrawerOpen}
                onClose={() => setIsSettingsDrawerOpen(false)}
              />
            )}
          </div>
        </nav>
      </div>
    </div>
  )
}
